import type { FilterRuleModel } from '@/shared/generated'

import React, { Suspense, useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { assortmentQueries, unwrapLines } from '@/entities/assortment'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Operators } from '@/shared/components/query-builder'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Sheet, SheetContent } from '@/shared/components/ui/sheet'
import { Spinner } from '@/shared/components/ui/spinner'
import { useStore } from '@/shared/lib/datasheet/store'

import { isProductEntity } from '../lib/helpers'
import { SkuSidebarContent } from './SkuSidebarContent'
import { SkuSidebarHeader } from './SkuSidebarHeader'

export function SkuSidebar() {
	const isOpen = useStore((state) => state.isSidebarOpen)
	const setIsSkuPanelOpen = useStore((state) => state.setIsSidebarOpen)
	const setSkuPanelContent = useStore((state) => state.setSidebarContent)
	const content = useStore((state) => state.sidebarContent)

	const handleClose = () => {
		setIsSkuPanelOpen(false)
		setTimeout(() => {
			setSkuPanelContent(null)
		}, 100)
	}

	return (
		<Sheet
			modal={false}
			defaultOpen={isOpen}
			open={isOpen}
			onOpenChange={(open) => !open && handleClose()}
		>
			<SheetContent
				onPointerDownOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => e.preventDefault()}
				disbleOverlay
				className='inset-y-2 right-2 h-auto w-150 gap-0 rounded-3xl border bg-background/80 backdrop-blur-xl sm:max-w-150'
			>
				<ErrorBoundary>
					<Suspense fallback={<SkuLoadingPlaceholder />}>
						{content && (
							<SkuSidebarInner
								lineId={content.line_id}
								optimizationGroupId={content.optimization_group_id}
								sku={isProductEntity(content) ? content.sku : undefined}
							/>
						)}
					</Suspense>
				</ErrorBoundary>
			</SheetContent>
		</Sheet>
	)
}
const SkuLoadingPlaceholder = () => {
	const { t } = useTranslation()

	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<Spinner />
				</EmptyMedia>
				<EmptyDescription>{t('loading')}</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}
const SkuSidebarInner = ({
	lineId,
	optimizationGroupId,
	sku,
}: {
	lineId: string
	optimizationGroupId: string
	sku?: string
}) => {
	const filters = useMemo(
		(): FilterRuleModel[] => [
			{
				name: 'line_id',
				operation: Operators.IS,
				value: lineId,
			},
			{
				name: 'optimization_group_id',
				operation: Operators.IS,
				value: optimizationGroupId,
			},
		],
		[lineId, optimizationGroupId]
	)

	const { data } = useSuspenseQuery(
		assortmentQueries.list({
			filters,
			limit: 1,
		})
	)
	const content = useMemo(() => {
		if (sku) {
			return unwrapLines(data.data)[0]
		} else {
			return data.data[0]
		}
	}, [data.data, sku])

	return (
		<ErrorBoundary>
			<SkuSidebarHeader content={content!} />
			<SkuSidebarContent content={content!} />
		</ErrorBoundary>
	)
}
